import { useMutation } from "react-query";

import type { PushDeliveryEventType } from "@jti/fe-common";
import {
  type MessagingSubscribePayload,
  type MessagingSubscribeResponse,
  type MessagingUnsubscribePayload,
} from "@jti/rest-api-types";

import { MUTATION_KEYS } from "../../../constants/queryKeys";
import { MessagingApi, type RestResponseType } from "../../../services";

type UseMessaging = {
  subscribeUser: (subscribePayload: MessagingSubscribePayload) => Promise<RestResponseType<MessagingSubscribeResponse>>;
  unsubscribeUserFully: () => Promise<RestResponseType>;
  unsubscribeUser: (unsubscribePayload: MessagingUnsubscribePayload) => Promise<RestResponseType>;
  reportDeliveryEvent: (
    reference: string,
    eventType: PushDeliveryEventType,
    metadata?: Record<string, unknown>
  ) => Promise<void>;
};

export const useMessaging = (): UseMessaging => {
  const messagingApi = MessagingApi.init();

  const { mutateAsync: subscribeUser } = useMutation(
    MUTATION_KEYS.subscribeUser,
    (payload: MessagingSubscribePayload) => messagingApi.subscribeUser(payload)
  );

  const { mutateAsync: unsubscribeUserFully } = useMutation(MUTATION_KEYS.unsubscribeUserFully, () =>
    messagingApi.unsubscribeFromEverything()
  );

  const { mutateAsync: unsubscribeUser } = useMutation(
    MUTATION_KEYS.unsubscribeUser,
    (unsubscribePayload: MessagingUnsubscribePayload) => messagingApi.unsubscribe(unsubscribePayload)
  );

  const reportDeliveryEvent = (
    reference: string,
    eventType: PushDeliveryEventType,
    metadata?: Record<string, unknown>
  ) => messagingApi.reportDeliveryEvent(reference, eventType, metadata);

  return { subscribeUser, unsubscribeUserFully, unsubscribeUser, reportDeliveryEvent };
};
